import { ChakraV3 } from '@piccolohealth/ui';
import { P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegCheckCircle } from 'react-icons/fa';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { useAuth } from '../../context/AuthContext';
import { zodResolver } from '../../utils/zod';
import { AuthCard } from './components/AuthCard';
import { AuthLayout } from './components/AuthLayout';
import { BackToLoginButton } from './components/BackToLoginButton';

type FormValues = {
  email: string;
};

const schema: z.ZodSchema<FormValues> = z.object({
  email: z.email('Invalid email').min(1, 'Email is required'),
});

export const RequestResetPassword = () => {
  const { requestResetPasswordEmail } = useAuth();

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const email = methods.watch('email');

  const onSubmit = async (data: FormValues) => {
    try {
      await requestResetPasswordEmail(data.email);
    } catch (error) {
      methods.setError('email', { message: 'Failed to request password reset. Please try again.' });
    }
  };

  const content = P.run(() => {
    if (methods.formState.isSubmitSuccessful) {
      return (
        <AuthCard>
          <ChakraV3.Stack gap={4} align='center'>
            <ChakraV3.Icon color='success' boxSize={16}>
              <FaRegCheckCircle />
            </ChakraV3.Icon>
            <ChakraV3.Text fontSize='sm' textAlign='center'>
              If an account exists for {email}, we have sent instructions to reset your password.
            </ChakraV3.Text>
          </ChakraV3.Stack>
        </AuthCard>
      );
    }

    return (
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Reset Password
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Enter your email address and we&apos;ll send you instructions to reset your password
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <ChakraV3.Stack as='form' gap={4} onSubmit={methods.handleSubmit(onSubmit)}>
            <HookedFormField name='email' label='Email'>
              <HookedInput name='email' size='md' placeholder='Enter email' />
            </HookedFormField>
            <HookedSubmitButton size='md' mt={4} loadingText='Sending reset instructions...'>
              Send Reset Instructions
            </HookedSubmitButton>
          </ChakraV3.Stack>
          <BackToLoginButton />
        </FormProvider>
      </AuthCard>
    );
  });

  return <AuthLayout>{content}</AuthLayout>;
};
